/**
 * Native <dialog> handlers: data-dialog-open opens, data-dialog-close closes.
 */

// Must stay in step with the Intent registry (app/components/intent.rb); `info` is the no-risk
// fallback. Only what this module swaps: the glyphs arrive pre-coloured from the component.
const INTENTS = {
  danger: { button: 'btn-danger', tint: 'tw:bg-danger/10', accent: 'tw:border-danger' },
  warning: { button: 'btn-warning', tint: 'tw:bg-warning/10', accent: 'tw:border-warning' },
  info: { button: 'btn-primary', tint: 'tw:bg-primary/10', accent: 'tw:border-primary' },
};

// Turbo submits a data-turbo-method link through a synthesized form and calls
// requestSubmit() with no submitter, so a link trigger has to be captured here
// or its risk style is lost by the time confirm() runs.
let lastConfirmTrigger = null;

/**
 * The shell takes its intent from the trigger, so one signal drives the confirm
 * button and the medallion together, as it does for a rendered dialog.
 *
 * A trigger styled as a button carries its risk in that class. One that is not a
 * button, such as a link in a danger zone, has to say so with data-confirm-intent,
 * or it would open a destructive action behind a no-risk confirm.
 *
 * @param {Element|null|undefined} element the trigger / form submitter
 * @returns {string} the intent's name, a key of INTENTS
 */
function confirmIntentFor(element) {
  if (element) {
    const declared = element.dataset?.confirmIntent;
    if (declared && Object.hasOwn(INTENTS, declared)) return declared;

    for (const [name, intent] of Object.entries(INTENTS))
      if (element.classList.contains(intent.button)) return name;
  }

  return 'info';
}

/**
 * Restyles the shell, which the component rendered for one fixed intent.
 *
 * The glyph is swapped rather than recoloured: the shell renders every intent's icon in its
 * own colour and hides all but the active one, because tinting a triangle cannot round it.
 *
 * @param {HTMLDialogElement} dialogEl
 * @param {string} name the intent's name, a key of INTENTS
 * @returns {void}
 */
function applyIntent(dialogEl, name) {
  const intent = INTENTS[name];
  const medallion = dialogEl.querySelector('[data-dialog-medallion]');
  const panel = dialogEl.querySelector('[data-dialog-panel]');

  for (const known of Object.values(INTENTS)) {
    medallion?.classList.remove(known.tint);
    panel?.classList.remove(known.accent);
  }

  medallion?.classList.add(intent.tint);
  panel?.classList.add(intent.accent);

  // Empty on a dialog rendered for one fixed intent, which has nothing to switch between.
  for (const glyph of medallion?.querySelectorAll('[data-dialog-medallion-glyph]') ?? [])
    glyph.classList.toggle('tw:hidden', glyph.dataset.dialogMedallionGlyph !== name);
}

/** One dialog instance. */
export class Dialog {
  constructor(element) {
    if (!(element instanceof HTMLDialogElement))
      throw new Error('Dialog must be initialized with an HTMLDialogElement');

    this.element = element;
  }

  open() {
    if (this.element.open) return;

    this.element.showModal();
    this.setupTypedConfirmationIfNeeded();
    this.element.addEventListener("close", () => this.teardownTypedConfirmation(), { once: true });
  }

  close() {
    this.element.close();
  }

  /**
   * Enables the submit only while the input matches data-dialog-confirm-value,
   * case-insensitively. Reset on each open.
   */
  setupTypedConfirmationIfNeeded() {
    const form = this.element.querySelector("form[data-dialog-confirm-value]");
    if (!form) return;

    const input = form.querySelector("[data-dialog-confirm-input]");
    const submitBtn = form.querySelector("[data-dialog-confirm-submit]");
    if (!input || !submitBtn) return;

    this.teardownTypedConfirmation(); // Remove any existing listeners (if any)

    input.disabled = false;
    input.value = "";
    submitBtn.disabled = true;

    const expected = (form.getAttribute("data-dialog-confirm-value") || "").trim().toLowerCase();
    const check = () => {
      submitBtn.disabled = input.value.trim().toLowerCase() !== expected;
    };
    form._dialogConfirmCheck = check;
    input.addEventListener("input", check);
  }

  /** Removes the typed-confirmation listeners and disables the input. */
  teardownTypedConfirmation() {
    const form = this.element.querySelector("form[data-dialog-confirm-value]");
    if (!form?._dialogConfirmCheck) return;

    const input = form.querySelector("[data-dialog-confirm-input]");
    if (input) {
      input.removeEventListener("input", form._dialogConfirmCheck);
      input.disabled = true;
    }
    delete form._dialogConfirmCheck;
  }

  /**
   * The controller for a dialog element, created once and cached on it.
   *
   * @param {HTMLDialogElement} element
   * @returns {Dialog}
   */
  static for(element) {
    if (!element.__controller) element.__controller = new Dialog(element);

    return element.__controller;
  }

  static ready() {
    document.addEventListener("click", (event) => {
      lastConfirmTrigger = event.target?.closest?.("[data-turbo-confirm]") || null;
    }, true);

    // Delegated, so elements added or moved by other components still work.
    document.addEventListener("click", (event) => {
      const hit = event.target.closest("[data-dialog-open],[data-dialog-close]");
      if (!hit) return;

      event.preventDefault();

      const opening = hit.hasAttribute("data-dialog-open");
      const dialog = opening
        ? document.querySelector(hit.getAttribute("data-dialog-open"))
        : hit.closest("dialog");
      if (!dialog) return;

      if (opening) Dialog.for(dialog).open();
      else Dialog.for(dialog).close();
    });

    Dialog.openMarked();
    // A Turbo visit swaps in a <body> this module never scanned, so the markers
    // that arrive with it need the same pass a full page load gets.
    document.addEventListener("turbo:load", () => Dialog.openMarked());
  }

  /**
   * Opens every dialog the server marked to open on arrival. Idempotent: `open()`
   * returns early for a dialog that is already open.
   *
   * @returns {void}
   */
  static openMarked() {
    document.querySelectorAll("dialog[data-dialog-open-now]").forEach(dialog => Dialog.for(dialog).open());
  }

  /**
   * Opens the shared confirm shell, resolving true on confirm and false on cancel.
   * The trigger opts in with data-turbo-confirm, plus optional data-confirm-title
   * and data-confirm-label. A pending confirmation is superseded, resolving false.
   *
   * @param {string} message the confirmation message
   * @param {Element|null} submitter the control that triggered the confirmation
   * @returns {Promise<boolean>}
   */
  static confirm(message, submitter) {
    const trigger = submitter || lastConfirmTrigger;
    lastConfirmTrigger = null;

    const dialogEl = document.getElementById('confirm-dialog');
    // The heading is the component's, so follow the label it advertises.
    const titleEl = document.getElementById(dialogEl?.getAttribute('aria-labelledby'));
    const messageEl = dialogEl?.querySelector('[data-confirm-dialog-message]');
    const confirmBtn = dialogEl?.querySelector('[data-confirm-dialog-confirm]');
    const cancelBtn = dialogEl?.querySelector('[data-confirm-dialog-cancel]');

    // MIGRATION (#35411): drop once every layout renders the confirm shell.
    if (!dialogEl || !messageEl || !confirmBtn)
      return Promise.resolve(window.confirm(message));

    // Supersede any pending confirmation so a second trigger never stacks listeners.
    dialogEl.__confirmAbort?.();

    const intentName = confirmIntentFor(trigger);

    if (titleEl) titleEl.textContent = trigger?.getAttribute('data-confirm-title') || 'Please confirm';
    messageEl.textContent = message;
    confirmBtn.textContent = trigger?.getAttribute('data-confirm-label') || 'Confirm';
    confirmBtn.className = `btn ${INTENTS[intentName].button}`;
    applyIntent(dialogEl, intentName);

    const controller = Dialog.for(dialogEl);

    return new Promise((resolve) => {
      const settle = (value) => {
        confirmBtn.removeEventListener('click', onConfirm);
        dialogEl.removeEventListener('close', onClose);
        delete dialogEl.__confirmAbort;
        resolve(value);
      };

      const onConfirm = () => {
        settle(true);
        controller.close();
      };

      const onClose = () => settle(false);

      dialogEl.__confirmAbort = () => settle(false);
      confirmBtn.addEventListener('click', onConfirm);
      dialogEl.addEventListener('close', onClose);

      controller.open();
      cancelBtn?.focus();
    });
  }
}

export default Dialog;
